import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, HStack, Spinner, Text, useColorMode } from '@chakra-ui/react';
import { ArrowBackIcon, ArrowForwardIcon, CheckIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../hooks/isMobile';
import { useIsTablet } from '../../../hooks/isTablet';
import { colors } from '../../../App/ChakraTheme';

interface ContentNavigationProps {
  isBackDisabled: boolean;
  currentSectionCounter: number;
  totalSections: number;
  isNextDisabled: boolean;
  isChalkupsLoading: boolean;
  isChalkedUp: boolean;
  toggleComplete: (value: boolean) => void;
  goToTop: () => void;
  next: () => void;
  back: () => void;
  onHoverNext?: () => void;
  onHoverBack?: () => void;
  uncompletable?: boolean;
  isUsingMaxWidth?: boolean;
  secondRow?: boolean;
  isAtEndOfUnit?: boolean;
  backToProgramUrl?: string;
  isOnLastPage?: boolean;
}

const ContentNavigation: React.FC<ContentNavigationProps> = (props) => {
  const {
    isBackDisabled,
    currentSectionCounter,
    totalSections,
    isNextDisabled,
    isChalkedUp,
    isChalkupsLoading,
    goToTop,
    toggleComplete,
    next,
    back,
    onHoverBack,
    onHoverNext,
    uncompletable,
    secondRow,
    isAtEndOfUnit,
    backToProgramUrl,
    isOnLastPage,
  } = props;
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const navButtonStyle = {
    size: isMobile || isTablet ? 'sm' : 'md',
    fontSize: isMobile || isTablet ? '10px' : '18px',
    padding: isMobile || isTablet ? '5px' : '20px',
  };


  return (
    <HStack
      justifyContent="space-between"
      py={secondRow ? 8 : 2}
      pb={8}
      className="content-nav-row"
    >
      <Button
        colorScheme="blue"
        disabled={isBackDisabled}
        onClick={() => back()}
        onMouseEnter={() => { if (onHoverBack) onHoverBack(); }}
        leftIcon={<ArrowBackIcon />}
        {...navButtonStyle}
      >
        {window.innerWidth < 399.99 ? '' : t('contentNavigation.previous')}
      </Button>

      <Box
        role="progressbar"
        aria-valuenow={currentSectionCounter}
        aria-valuemin={0}
        aria-valuemax={totalSections}
        aria-label={`${currentSectionCounter} of ${totalSections} sections`}
        position="relative"
        flexBasis="50%"
        maxW="45%"
        h={6}
        borderRadius="8px"
        overflow="hidden"
        style={{ backgroundColor: isDark ? '#334155' : '#e2e8f0' }}
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          bottom={0}
          borderRadius="8px"
          style={{
            width: `${totalSections > 0 ? (currentSectionCounter / totalSections) * 100 : 0}%`,
            backgroundColor: '#75d640',
            transition: 'width 0.3s ease',
          }}
        />
        <Text
          as="span"
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          textAlign="center"
          color={isDark ? 'white' : 'black'}
          fontSize="sm"
          fontWeight="600"
          lineHeight="24px"
          zIndex={1}
        >
          {currentSectionCounter} / {totalSections}
        </Text>
      </Box>

      {totalSections > 0 &&
      ((isNextDisabled && !uncompletable) ||
        (isAtEndOfUnit && backToProgramUrl && isOnLastPage)) ? (
        isChalkupsLoading ? (
          <Spinner />
        ) : (
          <HStack spacing={3}>
            {!isAtEndOfUnit && (
              <Button
                colorScheme="green"
                variant={isChalkedUp ? 'solid' : 'outline'}
                onClick={() => { toggleComplete(true); goToTop(); }}
                rightIcon={<CheckIcon />}
                {...navButtonStyle}
                sx={
                  isChalkedUp
                    ? {
                        backgroundColor: `${colors.limegreen} !important`,
                        borderColor: `${colors.limegreen} !important`,
                        color: 'white !important',
                        '&:hover': { opacity: '0.9 !important' },
                      }
                    : {
                        borderColor: `${colors.limegreen} !important`,
                        color: isDark ? `${colors.limegreen} !important` : '#276749 !important',
                        '&:hover': {
                          backgroundColor: `${colors.limegreen} !important`,
                          color: 'white !important',
                        },
                      }
                }
              >
                {isChalkedUp
                  ? t('contentNavigation.contentCompleted')
                  : t('contentNavigation.markAsComplete')}
              </Button>
            )}
            {isAtEndOfUnit && isChalkedUp && backToProgramUrl && (
              <Button
                as={Link}
                to={backToProgramUrl}
                colorScheme="blue"
                sx={{
                  textDecoration: 'none !important',
                  '&:hover': { textDecoration: 'none !important', color: 'white !important' },
                }}
                {...navButtonStyle}
              >
                {t('contentNavigation.backToClassroom')}
              </Button>
            )}
            {isAtEndOfUnit && !isChalkedUp && (
              <Button
                colorScheme="green"
                variant="outline"
                onClick={() => { toggleComplete(true); goToTop(); }}
                rightIcon={<CheckIcon />}
                {...navButtonStyle}
                sx={{
                  borderColor: `${colors.limegreen} !important`,
                  color: isDark ? `${colors.limegreen} !important` : `${colors.limegreen} !important`,
                  '&:hover': {
                    backgroundColor: `${colors.limegreen} !important`,
                    color: 'white !important',
                  },
                }}
              >
                {t('contentNavigation.markAsComplete')}
              </Button>
            )}
          </HStack>
        )
      ) : (
        <Button
          colorScheme="blue"
          disabled={isNextDisabled}
          onClick={() => next()}
          onMouseEnter={() => { if (onHoverNext) onHoverNext(); }}
          {...navButtonStyle}
        >
          <Text mr={2} mb="unset">
            {window.innerWidth < 399.99 ? '' : t('contentNavigation.next')}
          </Text>
          <ArrowForwardIcon />
        </Button>
      )}
    </HStack>
  );
};

export default ContentNavigation;
